<template>
    <MonitorLayoutTemplate
        table_title="國家風景區管理處資訊"
        :show_search_text="false"
    >
        <template #datetime>
            <MonitorOptionSet 
                ref="monitor_option_set"
                :option_set="option_set"
                @filter_data="filter_data"
            >
                <template v-if="option_set.length !== 0" 
                    #remarks 
                />
            </MonitorOptionSet>
        </template>

        <template #description>
            <div class="signs__description">
                <span v-for="([text, icon]) in Object.entries(icons)" 
                    :key="text"
                >
                    <v-icon :class="`multiple-signal--${icon.color}`">
                        {{ icon.name }}
                    </v-icon>
                    {{ text }}
                </span>
            </div>
        </template>

        <template #signs>
            <MonitorMultipleSigns v-if="sign_data"
                :data="sign_data"
            />
        </template>

        <template #data_table>
            <ScenicAreaMonitor
                :key="default_value_weather"
                :default_value="default_value_weather"
                :table_data="table_data_weather"
                :show_download_pdf_btn="true"
                @transfer_immutable_table_data="get_immutable_table_data_weather"
            />
            <ScenicAreaMonitor
                :key="default_value_buoy"
                :default_value="default_value_buoy"
                :table_data="table_data_buoy"
                :show_download_pdf_btn="true"
                @transfer_immutable_table_data="get_immutable_table_data_buoy"
            />
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import { 
        add_gust_interval_label_callback,
        sort_by_column
    } from '@/utils/monitor/common_tool.js'
    import {
        generate_variable_options,
        combine_new_variable_options,
        update_option_set_from_local_storage,
        update_current_option_value,
    } from '@/utils/monitor/option.js'
    import { 
        icons,
        reconstruct_signs_structure,
        merge_data_by_item,
        merge_by_property,
    } from "@/utils/monitor/customization/scenic.js"
    import {
        reconstruct_alert_icon,
    } from '@/utils/monitor/multiple_signs.js'
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import MonitorMultipleSigns from "@/components/monitor/_tool/monitor_multiple_signs.vue";
    import MonitorOptionSet from "@/components/monitor/monitor_option_set.vue";
    import ScenicAreaMonitor from "@/components/monitor/common/scenic_area_monitor.vue"
    
    export default {
        name: 'ScenicAreaManagement',

        components: {
            MonitorLayoutTemplate,
            MonitorMultipleSigns, 
            MonitorOptionSet,
            ScenicAreaMonitor
        },

        data() {
            return {
                immutable_table_data_weather: [],
                immutable_table_data_buoy: [],
                constructed_alert_data_weather: [],
                constructed_alert_data_buoy: [],
                table_data_weather: [],
                table_data_buoy: [],

                option_set: [],

                default_value_weather: 160,
                default_value_buoy: 157,

                icons: icons,

                local_storage_option_name: 'scenic_area_management_option',
            }
        },
        
        computed: {
            sign_data() {
                if (this.table_data_weather.length !== 0 || this.table_data_buoy.length !== 0) {   
                    const sign_data_weather = reconstruct_signs_structure(merge_data_by_item(this.table_data_weather))
                    const sign_data_buoy = reconstruct_signs_structure(merge_data_by_item(this.table_data_buoy))
                    return merge_by_property(sign_data_weather, sign_data_buoy, "idx", ["sign_icons"])
                        .sort(sort_by_column('idx_for_sort'))
                }

                return []
            },

            immutable_table_data() {
                return [...this.immutable_table_data_weather, ...this.immutable_table_data_buoy];
            },
        },


        watch: {
            immutable_table_data: {
                handler() {
                    if (this.immutable_table_data_weather.length !== 0 && this.immutable_table_data_buoy.length !== 0) {
                        this.generate_option_set();
                        this.option_set = update_option_set_from_local_storage(this.option_set , this.local_storage_option_name)
                    }
                },
                deep: true
            },
        },
        
        methods: {
            get_immutable_table_data_weather(data_from_child) {
                const has_gust_interval_label_data = data_from_child.map(add_gust_interval_label_callback('瞬間風(級)'))
                this.immutable_table_data_weather = has_gust_interval_label_data
                this.constructed_alert_data_weather = reconstruct_alert_icon(has_gust_interval_label_data, icons)
                    .sort(sort_by_column('項次'))
                    .sort(sort_by_column('alert_order'))
            },
            
            get_immutable_table_data_buoy(data_from_child) {
                this.immutable_table_data_buoy = data_from_child
                this.constructed_alert_data_buoy = reconstruct_alert_icon(data_from_child, icons)
                    .sort(sort_by_column('項次'))
                    .sort(sort_by_column('alert_order'))
            },

            generate_option_set() {
                const basic_variable_options = [
                    { json_key: '風景管理處', label: 'management_office', description: '國家風景區管理處' },
                ]

                const variable_options = generate_variable_options(basic_variable_options, this.immutable_table_data)

                if (this.option_set.length === 0){
                    this.option_set = variable_options
                } else {
                    this.option_set = combine_new_variable_options(this.option_set, variable_options)
                    this.$refs.monitor_option_set.change_option_set()
                }
            },

            filter_data(filtered_condition) {
                const update_current_value_by_filtered_condition = update_current_option_value(filtered_condition)
                this.option_set = this.option_set.map(update_current_value_by_filtered_condition)

                const compare_management_office = filtered_condition['management_office']
                const filter_by_management_office = item => item['風景管理處'] === compare_management_office
                this.table_data_weather = this.constructed_alert_data_weather.filter(filter_by_management_office)
                this.table_data_buoy = this.constructed_alert_data_buoy.filter(filter_by_management_office)

                // 把目前選擇的 option 存起來
                localStorage.setItem(this.local_storage_option_name, JSON.stringify(filtered_condition))

            },
        },

    }
</script>

<style lang="scss" scoped>
    @import '../../../../css/monitor/sign';

    .signs {
        &__description {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
        }
    }
</style>